/**
 * **What a failed cloud attempt licenses now — the classification half of PEC-13.**
 *
 * `autoPick.ts` **licenses** a fallback; this module **classifies** one, and the split is that
 * file's own: *"the fallback is licensed here and classified at the screen, which is the only
 * place that can see whether the failed request ever reached the network."* This is that
 * classification lifted out of two screens into one copy — **not** folded into `pickTransport`,
 * which reads a `PickInput` of five pure fields and must keep doing so. `decideFallback` reads a
 * {@link CloudVerdict}, the wall clock and the Capacitor bridge; putting any of that behind
 * `pickTransport` would give the picker the in-flight visibility PEC-13's structure exists to
 * refuse, and `autoPick.test.ts` pins its key set to stop exactly that.
 *
 * It was two copies until D240. Home's `settleFallback` and Gate Detail's `offerOrNothing` carried
 * the same rule, the same `GESTURE_BUDGET_MS`, and the same `radioReachableNow`, and `autoPick.ts`
 * names the failure mode that invites: *"it would also put the two screens on different rules for
 * the same decision."* One decision, read twice, is what this file is.
 *
 * **D240 — the container has no offer, in either direction.** The offer is a web-platform
 * artefact and a consent control, and only the second of those survives the move to native:
 *
 *  - **Warm credential → dispatch, `ambiguous` included.** The second tap bought a chooser
 *    (`requestDevice` needs transient activation, D189(c)) and a consent decision. The container
 *    has no chooser — {@link radioReachableNow} already answered `true` there before this
 *    decision — so what is left is the consent, and D240 takes it. 🔸 **That is an override of
 *    PEC-8's *never manufacture a second attempt*, not a satisfaction of it**: on an `ambiguous`
 *    verdict the cloud command may have reached the gate, and the client now sends a second one
 *    without asking. D223 keeps the loudest cases away from here — `gate-busy`, `open-in-flight`
 *    and `rejected` are Success and leave `verdict` null — and a second `CMD:OpenN` against a
 *    moving leaf is answered `CMD:Err`, which D216/D223 route to Success. *Usually benign* is not
 *    the rule PEC-8 states, which is why the parent's D240 row says it is overriding it.
 *  - **Cold credential → nothing runs and nothing asks.** A cold local open needs an `ble-access`
 *    round trip over the network that just failed, so the offer led to a refusal rather than to an
 *    open (D189(d)/D217). Offering a tap that cannot succeed is worse than saying nothing, and the
 *    cloud failure's own sentence is the honest terminal answer — which is what D223 designated it
 *    as. The accepted cost is the case where reception returned between the failure and the tap;
 *    it worked, it was never counted, and D227's credential persistence is what shrinks it.
 *
 * **The web leaf is D189(b)/(f) verbatim.** Nothing below changes what a PWA does, and the offer's
 * whole rendering — `fallbackOffer`, `.offering`, `FALLBACK_OFFER_MS`, the three i18n keys — stays
 * live there and merely unreachable in the container. It is not dead code; it is code with one
 * platform left.
 */

import type { CloudVerdict, TransportPlan } from './autoPick'
import { usesNativeBle } from './nativeBluetooth'
import { userActivationLive } from './webBluetooth'

/**
 * 🔸 **How recent a tap has to be for an automatic BLE fallback to still reach the radio.** D189(c).
 *
 * Chrome gates the Bluetooth chooser on *transient user activation* — roughly five seconds, which
 * `requestDevice` also consumes — while `REQUEST_TIMEOUT_MS` is twenty. So a fallback fired after a
 * cloud request ran its full budget cannot prompt, and would report *"couldn't reach the gate over
 * Bluetooth"* about a radio that was never switched on. This is the belt to `userActivationLive()`'s
 * braces: that API answers precisely where it exists, and this bounds the case where it does not.
 *
 * **Deliberately well under the platform's own window**, because being early costs one extra tap
 * on a button that is offered anyway, and being late costs a false statement about the gate.
 * It is not measured on a device; only the spec's number is known, and this is not that number.
 */
const GESTURE_BUDGET_MS = 3_000

/**
 * **Whether an automatic fallback could actually reach the radio** — D189(c).
 *
 * See `userActivationLive()`: getting this wrong does not produce a failed fallback, it produces a
 * *false statement about the gate* — *"could not reach the gate over Bluetooth"* about a radio
 * that was never switched on — which is what D134/D135 forbid. The container has no chooser, so it
 * has nothing to be allowed.
 */
export function radioReachableNow(tappedAt: number): boolean {
  if (usesNativeBle()) return true
  return userActivationLive() ?? Date.now() - tappedAt < GESTURE_BUDGET_MS
}

/**
 * The three things a screen may do with a failed cloud attempt.
 *
 * A union rather than a boolean pair, so neither caller re-narrows `verdict` for itself: the
 * verdict travels **inside** the two acting arms, and `dispatchLocal` needs it (D217 — a
 * `never-dispatched` verdict suppresses the expiry check, and `ambiguous` must not).
 */
export type FallbackDecision =
  | { readonly act: 'nothing' }
  | { readonly act: 'dispatch'; readonly verdict: CloudVerdict }
  | { readonly act: 'offer'; readonly verdict: CloudVerdict }

/**
 * **D273 — whether a COLD credential may carry a fallback, on the one path where it may.**
 *
 * D240 (b) withheld the container's cold fallback on a stated reason: *"a cold local open needs an
 * `ble-access` round trip over the network that just failed."* On D264 (h)'s diverted tap that
 * reason is **false rather than merely weaker**. That tap exists because the offline latch may be
 * stale; if the server answered it, the latch **was** stale, the network is up, and the round trip
 * D240 refused is the one the attempt has just proved viable. So the licence is not a softening of
 * D240 — it is D240's own test, applied to a path where it now returns the other answer.
 *
 * **`'withheld'` is every other path**, D240 (b) included and unchanged: an ordinary online cold
 * tap that fails still runs nothing and asks nothing in the container. Widening it there is a
 * different decision about a different clause and is not taken here.
 */
export type ColdFallbackLicence = 'licensed' | 'withheld'

/**
 * The two facts that license a cold fallback, named here so a screen supplies facts rather than
 * inventing the rule — `attemptsCloudWhileLatched`'s own split.
 *
 * **`cloudAnswered` is *a response came back*, not *the open succeeded*.** A `200` carrying
 * `outcome: 'failed'` answers it, and so does a refusal; a `NetworkError` and the client's own
 * `REQUEST_TIMEOUT_MS` do not, because neither proves the network is there — and on those two the
 * cold fetch would meet the same silence and end in `ble.localNoCredential`, which is a **worse**
 * sentence than the one it replaced. Conservative in the safe direction: anything that leaves the
 * attempt by throwing reads as *not answered*.
 */
export function coldFallbackLicence(
  latchedCloudAttempt: boolean,
  cloudAnswered: boolean,
): ColdFallbackLicence {
  return latchedCloudAttempt && cloudAnswered ? 'licensed' : 'withheld'
}

/**
 * Decide once, for this attempt, on this platform.
 *
 * `plan` is the plan the attempt was **locked to** before dispatch and must be threaded in rather
 * than recomputed (PEC-13): re-reading `credentialHeld` after the cloud attempt could change the
 * answer between locking a transport and acting on the lock.
 */
export function decideFallback(
  plan: TransportPlan,
  verdict: CloudVerdict | null,
  tappedAt: number,
  coldFallback: ColdFallbackLicence,
): FallbackDecision {
  // `verdict === null` means the cloud path answered for a reason a second transport cannot
  // improve on — an access refusal, or a D223 Success. Neither is offered anything.
  if (verdict === null || plan.fallback !== 'ble') return { act: 'nothing' }

  if (usesNativeBle()) {
    if (plan.fallbackReady || coldFallback === 'licensed') return { act: 'dispatch', verdict }
    return { act: 'nothing' }
  }

  if (verdict === 'never-dispatched' && plan.fallbackReady && radioReachableNow(tappedAt)) {
    return { act: 'dispatch', verdict }
  }
  return { act: 'offer', verdict }
}

/**
 * **D264 — whether a tap taken while the client is latched offline should try the cloud anyway.**
 *
 * `online: false` is a latch that only a *successful* call clears, and the browser `online` event
 * that was supposed to clear it does not always fire (`SessionApi.recoverConnectivity` carries the
 * account). While it is stuck, `pickTransport` answers `primary: 'ble'` for every tap — so a gate
 * with **no local path at all** was refused without the client ever asking whether the network had
 * come back. This is the one arm that changes: a held credential still goes straight to the radio
 * with no wait in front of it, exactly as before.
 *
 * **The condition is `credentialHeld`, and it must not be read off `plan.primary`.** Offline,
 * `pickTransport` deliberately does not consult `credentialHeld` — it returns `'ble'` for any
 * usable radio plus a fresh snapshot, and the credential refusal happens a layer down inside
 * `withCredential`. So a branch written against the plan sends the **no-credential** gate to the
 * refuse-immediately side: the gate with nothing local to run, and the one this exists for. That
 * inversion is the whole reason this predicate takes three arguments rather than reading one.
 *
 * **Here rather than in `pickTransport`, for that file's own reason.** `autoPick.ts` licenses a
 * transport from five pure fields and `autoPick.test.ts` pins their key set; this reads the
 * session's latch, which is in-flight state the picker is structured to refuse. It is the same
 * split {@link decideFallback} sits on, applied before the attempt instead of after it.
 *
 * **PEC-13 holds by construction rather than by care, and D273 did not weaken it.** The two arms
 * are still exclusive — with a credential the tap never reaches the cloud — so no tap ever has two
 * transports in flight. What changed is the second half of the old sentence: *"without one there is
 * no local path to fall back to"* was a claim about the **credential**, and it was reasoning about
 * the wrong fact. There is no local path *without a round trip*; whether that round trip can run is
 * a question about the network, and the diverted cloud attempt is what answers it. So
 * {@link latchedCloudPlan} now carries `fallback: 'ble'` where the plan carried `null`, and the
 * fallback is still a **second attempt after a verdict** (D189(b)) rather than a second transport
 * beside the first.
 */
export function attemptsCloudWhileLatched(
  plan: TransportPlan,
  online: boolean,
  credentialHeld: boolean,
): boolean {
  if (online || plan.primary !== 'ble') return false
  return !credentialHeld
}

/**
 * **D273 — the plan a D264 (h) diverted tap is actually locked to.**
 *
 * `pickTransport` answered `{primary: 'ble', reason: 'offline', fallback: null}` for this tap,
 * which was true of the tap it described and **false of the tap that is actually made**: D264 (h)
 * sends this one to the cloud. So the plan threaded into {@link decideFallback} described a
 * different attempt, and its `fallback: null` bailed the decision out **before** the platform
 * branch — which is why neither leaf retried while latched, and why the resident's recovery cost a
 * whole second tap (R6 §2 F12).
 *
 * Three things this is careful about.
 *
 * **It is a derivation, not a second pick.** `pickTransport` is untouched, `PickInput` gains no
 * field, and `autoPick.test.ts`'s key-set pin still holds — the latch is in-flight state that file
 * is structured to refuse, which is the same reason {@link attemptsCloudWhileLatched} lives here
 * and not there. This is that split applied to the plan instead of to the predicate.
 *
 * **It is derived once, before dispatch, and threaded** (PEC-13). Recomputing it after the attempt
 * is the between-lock-and-act read the rule forbids.
 *
 * **`fallbackReady` is `false` and that is honest rather than cautious.** The field means *could
 * this run with no round trip*, and `latchedToCloud` is true only when no credential is held —
 * so `true` here would be a claim `holdsCredential()` contradicts. What licenses the cold fallback
 * is {@link ColdFallbackLicence}, which asks a different question about a different subsystem.
 *
 * `fallback: 'ble'` needs no condition: the offline `primary: 'ble'` variant is produced only when
 * BLE is usable **and** inside D61's window, which is exactly what the online branch tests before
 * it offers a fallback at all.
 */
export function latchedCloudPlan(plan: TransportPlan): TransportPlan {
  // Defensive rather than reachable: `attemptsCloudWhileLatched` answers `false` for every other
  // variant, so a caller can only arrive here with the offline one. Returning the plan unchanged
  // is what keeps a future second caller from silently inventing a cloud plan for a refusal.
  if (plan.primary !== 'ble') return plan
  return { primary: 'cloud', reason: 'default', fallback: 'ble', fallbackReady: false }
}
